import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api';
import { useAuth } from '../../AuthContext';
import { canViewAdminSettings } from '../../permissions';
import { BUDDIES, RARITIES, BUDDY_TUNING, pickRandomBuddyId, getBuddyById } from '../../buddy';

// Buddy admin surface. Was previously an inline card in SettingsPage; now its
// own route so Settings scrolls less and the page is deep-linkable.
export default function BuddyAdminPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setUsers(await api.listBuddyUsers()); setError(null); }
    catch (e) { setError(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { if (canViewAdminSettings(user)) load(); else setLoading(false); }, [user, load]);

  function now() { return Date.now(); }
  function newEggState(preferredId) {
    return {
      id: preferredId || pickRandomBuddyId(),
      stage: 'egg', hatchClicks: 0,
      lastFedAt: null, recentFeeds: [], hatchedAt: null, diedAt: null, nextEggAt: null,
    };
  }

  async function applyAction(u, nextState) {
    try { await api.setUserBuddy(u.username, nextState); await load(); }
    catch (e) { alert('Failed: ' + e.message); }
  }

  async function deleteUser(u) {
    if (!window.confirm(`Wipe ${u.username}'s buddy entirely?`)) return;
    try { await api.deleteUserBuddy(u.username); await load(); }
    catch (e) { alert('Failed: ' + e.message); }
  }

  function ageLabel(state) {
    if (!state.hatchedAt) return state.stage === 'egg' ? 'incubating' : '—';
    const ms = now() - state.hatchedAt;
    const h = Math.floor(ms / (60 * 60 * 1000));
    if (h < 1) return '< 1h old';
    if (h < 48) return `${h}h old`;
    return `${Math.floor(h / 24)}d old`;
  }

  if (!canViewAdminSettings(user)) {
    return (
      <div className="page-content">
        <div className="empty-state"><h3>Admin only</h3><p>Buddy admin is admin-only.</p></div>
      </div>
    );
  }

  return (
    <>
      <div className="top-bar">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Link to="/settings" className="btn btn-secondary btn-sm" style={{ textDecoration: 'none' }}>&larr; Settings</Link>
          <h2>Buddy Admin</h2>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}>
          {loading ? '…' : '↻ Refresh'}
        </button>
      </div>

      <div className="page-content">
        <div className="card">
          <div className="card-body">
            {error && <div style={{ color: 'var(--color-danger)', fontSize: '12px', marginBottom: '8px' }}>Error: {error}</div>}
            {!loading && users.length === 0 && (
              <div style={{ fontSize: '13px', color: '#777' }}>No users have a buddy yet. As users load the app, they&apos;ll show up here.</div>
            )}
            {users.map(u => {
              const buddy = getBuddyById(u.id);
              const rarity = RARITIES[buddy.rarity] || RARITIES.common;
              return (
                <div key={u.username} className="buddy-admin-row">
                  <div className="buddy-admin-sprite" style={{ borderColor: rarity.color }}>
                    <pre style={{ margin: 0, fontSize: '10px', lineHeight: 1.1 }}>{buddy.ascii}</pre>
                  </div>
                  <div className="buddy-admin-info">
                    <div className="buddy-admin-username">{u.username}</div>
                    <div className="buddy-admin-meta">
                      <strong>{buddy.name}</strong>
                      <span style={{ color: rarity.label }}> · {rarity.name}</span>
                      <span> · stage: <strong>{u.stage}</strong></span>
                      <span> · {ageLabel(u)}</span>
                    </div>
                  </div>
                  <div className="buddy-admin-actions">
                    <select value={u.id} onChange={e => applyAction(u, { ...u, id: e.target.value })} title="Change this user's buddy species">
                      {BUDDIES.map(b => (
                        <option key={b.id} value={b.id}>{b.name} ({RARITIES[b.rarity]?.name})</option>
                      ))}
                    </select>
                    <button className="btn btn-secondary btn-sm" onClick={() => applyAction(u, newEggState(u.id))} title="Reset to unhatched egg of same species">↺ Egg</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => applyAction(u, { ...u, stage: 'alive', hatchClicks: 0, hatchedAt: now(), lastFedAt: now(), diedAt: null, nextEggAt: null })} title="Skip the hatch, force-alive">⚡ Hatch</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => applyAction(u, { ...u, stage: 'fat', lastFedAt: now(), recentFeeds: Array.from({ length: BUDDY_TUNING.FAT_FEEDS_IN_WINDOW + 2 }, () => now()) })} title="Stuff them silly">🍖 Fat</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => applyAction(u, { ...u, stage: 'dead', diedAt: now(), nextEggAt: null, exitReason: 'starved' })} title="Kill — user clicks the grave to get a fresh egg">💀 Kill</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => applyAction(u, newEggState())} title="Random new egg (species rerolled)">🎲 Reroll</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => deleteUser(u)} title="Wipe entirely" style={{ color: '#dc2626' }}>× Wipe</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
